import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ObjectPreview, Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui';
import { TypedProperties } from './TypedField';
import { ObjectPropertyStrip, type ObjectStripItem } from './ObjectPropertyStrip';
import type { FieldDefinition, FieldState } from './typedFieldData';
import type { FieldActions, FieldCommit, FieldRegistry, SecretAccess } from './fieldRegistry';
export interface ObjectCardTab {
  id: string;
  label: string;
  count?: number;
  content: ReactNode;
}
export interface ObjectCardProps extends FieldActions {
  title: string;
  icon?: ReactNode;
  definitions: readonly FieldDefinition[];
  values: Readonly<Record<string, FieldState>>;
  strip?: readonly ObjectStripItem[];
  tabs?: readonly ObjectCardTab[];
  actions?: ReactNode;
  destructiveAction?: ReactNode;
  onClose?: () => void;
  onCommit?: FieldCommit;
  onRetry?: (id: string) => void;
  registry?: FieldRegistry;
  secrets?: Readonly<Record<string, SecretAccess>>;
  readOnly?: boolean;
  compact?: boolean;
  compactFieldIds?: readonly string[];
  /** Panel already supplies the accessible object heading. */
  hideTitle?: boolean;
  valueAlignment?: 'baseline' | 'first-line';
  /** Keep properties already represented in the metadata strip in one place. */
  omitStripProperties?: boolean;
  tabsOverflow?: 'default' | 'fade';
}
function FadingTabsList({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  useLayoutEffect(() => {
    const list = ref.current;
    if (!list) return;
    let active = true;
    const measure = () => {
      if (!active) return;
      const next = {
        start: list.scrollLeft > 1,
        end: list.scrollWidth - list.clientWidth - list.scrollLeft > 1,
      };
      setEdges((old) => (old.start === next.start && old.end === next.end ? old : next));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    for (const child of list.children) observer.observe(child);
    list.addEventListener('scroll', measure, { passive: true });
    measure();
    void document.fonts.ready.then(measure);
    return () => {
      active = false;
      observer.disconnect();
      list.removeEventListener('scroll', measure);
    };
  }, []);
  return (
    <div data-slot="object-tabs-scroll" className="relative min-w-0">
      <TabsList
        ref={ref}
        aria-label="Разделы объекта"
        className="scrollbar-none max-w-full justify-start overflow-x-auto overflow-y-hidden"
      >
        {children}
      </TabsList>
      {edges.start && (
        <span
          aria-hidden
          data-scroll-edge="start"
          className="pointer-events-none absolute inset-y-0 left-0 w-4 bg-linear-to-r from-card to-transparent"
        />
      )}
      {edges.end && (
        <span
          aria-hidden
          data-scroll-edge="end"
          className="pointer-events-none absolute inset-y-0 right-0 w-4 bg-linear-to-l from-card to-transparent"
        />
      )}
    </div>
  );
}
export function ObjectCard({
  title,
  icon,
  definitions,
  values,
  strip = [],
  tabs = [],
  actions,
  destructiveAction,
  onClose,
  onCommit,
  onRetry,
  registry,
  secrets,
  readOnly = false,
  compact = false,
  compactFieldIds,
  hideTitle = false,
  valueAlignment = 'baseline',
  omitStripProperties = false,
  tabsOverflow = 'default',
  ...fieldActions
}: ObjectCardProps) {
  const [tab, setTab] = useState('properties');
  const activeTab = tab === 'properties' || tabs.some((t) => t.id === tab) ? tab : 'properties';
  const inStrip = new Set(
    strip
      .filter((item) => definitions.some((d) => d.id === item.fieldId && 'options' in d))
      .map((item) => item.fieldId),
  );
  const selected = compact
    ? compactFieldIds
      ? definitions.filter((d) => compactFieldIds.includes(d.id))
      : definitions.slice(0, 3)
    : definitions;
  const shown = omitStripProperties ? selected.filter((d) => !inStrip.has(d.id)) : selected;
  const fields = (
    <TypedProperties
      definitions={shown}
      values={values}
      onCommit={compact ? undefined : onCommit}
      onRetry={onRetry}
      registry={registry}
      secrets={compact ? undefined : secrets}
      editable={!readOnly && !compact}
      valueAlignment={valueAlignment}
      {...fieldActions}
    />
  );
  const triggers = (
    <>
      <TabsTrigger value="properties">Свойства</TabsTrigger>
      {tabs.map((item) => (
        <TabsTrigger key={item.id} value={item.id} count={item.count}>
          {item.label}
        </TabsTrigger>
      ))}
    </>
  );
  return (
    <div data-slot="object-card" data-compact={compact ? '' : undefined} className="min-w-0">
      <ObjectPreview
        title={title}
        hideTitle={hideTitle}
        icon={icon}
        meta={
          <div className="w-full space-y-3">
            <ObjectPropertyStrip
              items={strip}
              definitions={definitions}
              values={values}
              onCommit={compact ? undefined : onCommit}
              readOnly={readOnly || compact}
            />
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
          </div>
        }
        destructiveAction={destructiveAction}
        onClose={onClose}
      >
        {compact ? (
          fields
        ) : (
          <Tabs value={activeTab} onValueChange={setTab}>
            {tabsOverflow === 'fade' ? (
              <FadingTabsList>{triggers}</FadingTabsList>
            ) : (
              <TabsList aria-label="Разделы объекта" className="max-w-full justify-start overflow-x-auto">
                {triggers}
              </TabsList>
            )}
            <TabsContent value="properties" className="pt-4">
              {fields}
            </TabsContent>
            {tabs.map((item) => (
              <TabsContent key={item.id} value={item.id} className="pt-4">
                {item.content}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </ObjectPreview>
    </div>
  );
}
